<template>
  <nav :class="['navbar-modern', { 'navbar-scrolled': isScrolled }]">
    <div class="navbar-inner">
      <router-link :to="languageStore.localizePath('/')" class="logo-link">
        <img src="@/assets/logo.png" alt="Efes Tech Logo" class="logo" width="123" height="36" />
      </router-link>

      <div class="nav-links desktop-only">
        <router-link :to="languageStore.localizePath('/')" class="nav-link">{{ navTexts.home }}</router-link>
        <router-link :to="languageStore.localizePath('/ozel-yazilim-gelistirme')" class="nav-link">{{ navTexts.customSoftware }}</router-link>
        <router-link :to="languageStore.localizePath('/projects')" class="nav-link">{{ navTexts.projects }}</router-link>
        <router-link :to="languageStore.localizePath('/blog')" class="nav-link">{{ navTexts.blog }}</router-link>
        <router-link :to="languageStore.localizePath('/about')" class="nav-link">{{ navTexts.about }}</router-link>
        <router-link :to="languageStore.localizePath('/faq')" class="nav-link">{{ navTexts.faq }}</router-link>
        <router-link :to="languageStore.localizePath('/contact')" class="nav-link">{{ navTexts.contact }}</router-link>
      </div>

      <div class="nav-actions">
        <router-link :to="languageStore.localizePath('/quote')" class="btn-quote desktop-only">
          {{ navTexts.quote }}
          <i class="pi pi-arrow-right"></i>
        </router-link>
        <button
          class="menu-toggle mobile-only"
          @click="toggleMenu"
          :aria-expanded="isMenuOpen"
          :aria-label="languageStore.currentLanguage === 'en' ? (isMenuOpen ? 'Close navigation menu' : 'Open navigation menu') : (isMenuOpen ? 'Menüyü kapat' : 'Menüyü aç')"
        >
          <span class="hamburger" :class="{ 'is-active': isMenuOpen }"></span>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition name="mobile-menu">
      <div class="mobile-nav" v-if="isMenuOpen">
        <router-link :to="languageStore.localizePath('/')" class="mobile-link" @click="closeMenu">{{ navTexts.home }}</router-link>
        <router-link :to="languageStore.localizePath('/ozel-yazilim-gelistirme')" class="mobile-link" @click="closeMenu">{{ navTexts.customSoftware }}</router-link>
        <router-link :to="languageStore.localizePath('/projects')" class="mobile-link" @click="closeMenu">{{ navTexts.projects }}</router-link>
        <router-link :to="languageStore.localizePath('/blog')" class="mobile-link" @click="closeMenu">{{ navTexts.blog }}</router-link>
        <router-link :to="languageStore.localizePath('/about')" class="mobile-link" @click="closeMenu">{{ navTexts.about }}</router-link>
        <router-link :to="languageStore.localizePath('/faq')" class="mobile-link" @click="closeMenu">{{ navTexts.faq }}</router-link>
        <router-link :to="languageStore.localizePath('/contact')" class="mobile-link" @click="closeMenu">{{ navTexts.contact }}</router-link>
        <router-link :to="languageStore.localizePath('/quote')" class="btn-quote mobile-quote" @click="closeMenu">
          {{ navTexts.quote }}
          <i class="pi pi-arrow-right"></i>
        </router-link>
      </div>
    </transition>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useLanguageStore } from '../../stores/language'

const isMenuOpen = ref(false)
const isScrolled = ref(false)
const languageStore = useLanguageStore()
const navTexts = computed(() => languageStore.nav)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

let ticking = false
const handleScroll = () => {
  if (!ticking) {
    window.requestAnimationFrame(() => {
      const scrolled = window.scrollY > 50
      if (isScrolled.value !== scrolled) {
        isScrolled.value = scrolled
      }
      ticking = false
    })
    ticking = true
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.requestAnimationFrame(() => {
    isScrolled.value = window.scrollY > 50
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.navbar-modern {
  position: fixed;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 92%;
  max-width: 1200px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.navbar-scrolled {
  top: 1rem;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(var(--primary-rgb), 0.08);
}

.navbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.6rem 0.6rem 2rem;
}

.logo-link {
  display: flex;
  align-items: center;
}

.logo {
  height: 36px;
  width: auto;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.nav-link {
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  transition: all 0.3s ease;
  position: relative;
}

.nav-link:hover {
  color: var(--accent-color);
  background: rgba(var(--primary-rgb), 0.05);
}

.nav-link.router-link-active {
  color: var(--primary-color);
  font-weight: 600;
}

.nav-actions {
  display: flex;
  align-items: center;
}

.btn-quote {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%);
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.8rem 1.6rem;
  border-radius: 999px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(var(--primary-rgb), 0.2);
  border: none;
}

.btn-quote:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(var(--primary-rgb), 0.3);
  color: white;
}

.btn-quote i {
  font-size: 0.9rem;
  transition: transform 0.3s ease;
}

.btn-quote:hover i {
  transform: translateX(4px);
}

.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

/* mobile hamburger styles */
.menu-toggle {
  background: rgba(var(--primary-rgb), 0.05);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.menu-toggle:hover {
  background: rgba(var(--primary-rgb), 0.1);
}

.hamburger {
  width: 20px;
  height: 2px;
  background: var(--primary-color);
  position: relative;
  transition: all 0.3s ease;
}
.hamburger::before,
.hamburger::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 2px;
  background: var(--primary-color);
  transition: all 0.3s ease;
  left: 0;
}
.hamburger::before { top: -6px; }
.hamburger::after { bottom: -6px; }

.hamburger.is-active { background: transparent; }
.hamburger.is-active::before { transform: rotate(45deg); top: 0; }
.hamburger.is-active::after { transform: rotate(-45deg); bottom: 0; }

/* Mobile Menu */
.mobile-nav {
  position: absolute;
  top: calc(100% + 1rem);
  left: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(var(--primary-rgb), 0.08);
}

.mobile-link {
  text-decoration: none;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  transition: background 0.3s ease;
  text-align: center;
}

.mobile-link:hover {
  background: rgba(var(--primary-rgb), 0.04);
  color: var(--accent-color);
}

.mobile-quote {
  margin-top: 0.5rem;
  border-radius: 16px;
  padding: 1rem;
  font-size: 1.05rem;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

@media (max-width: 992px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }
  .navbar-inner { padding: 0.5rem 1.25rem; }
  .navbar-modern { width: 95%; }
}
</style>
